import {
  Component,
  forwardRef,
  inject,
  OnDestroy,
  OnInit,
  HostBinding,
  input,
} from '@angular/core';

import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { DatePickerComponent } from '../date-picker/date-picker.component';
import { TimePickerComponent } from '../time-picker/time-picker.component';
import { Subscription } from 'rxjs';
import { LayoutService } from '../../../../core/services/layout.service';
import { DateTimeObject } from '../../../../core/models/datetime.model';

@Component({
  selector: 'app-datetime-picker',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DatePickerComponent,
    TimePickerComponent
],
  templateUrl: './datetime-picker.component.html',
  styleUrls: ['./datetime-picker.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatetimePickerComponent),
      multi: true,
    }],
})
export class DatetimePickerComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  private readonly fb = inject(FormBuilder);
  private readonly layoutService = inject(LayoutService);
  private subs = new Subscription();

  @HostBinding('class.is-below-tablet-layout')
  get isTabletOrBelow() {
    return (
      this.orientation() === 'vertical' || this.layoutService.isTabletOrBelow()
    );
  }

  orientation = input<'horizontal' | 'vertical'>('horizontal');

  // --- Internal State ---
  protected form: FormGroup;

  // --- CVA Methods ---
  onChange: (value: DateTimeObject | null) => void = () => {};
  onTouched: () => void = () => {};

  constructor() {
    this.form = this.fb.group({
      date: [null],
      time: [null, [TimePickerComponent.isValidTime()]],
    });
  }

  ngOnInit(): void {
    this.subs.add(
      this.form.valueChanges.subscribe((value) => {
        if (this.form.invalid) {
          this.onChange(null);
        } else {
          this.onChange(value);
        }
      })
    );
  }

  writeValue(value: DateTimeObject | null): void {
    if (value) {
      this.form.patchValue(value, { emitEvent: false });
    } else {
      this.form.reset({}, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: DateTimeObject | null) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
